import React, { useState } from 'react';
import type { Workflow, WorkflowStep, CreateWorkflowInput, TriggerConfig } from '../../lib/workflow-types';

interface Props {
  initialData?: Workflow;
  onSubmit: (data: CreateWorkflowInput) => void;
  onCancel?: () => void;
  loading?: boolean;
}

const STEP_TYPES = ['trigger', 'action', 'condition', 'transform', 'delay'];
const TRIGGER_TYPES = ['manual', 'schedule', 'webhook', 'agent_event'];

export function WorkflowEditor({ initialData, onSubmit, onCancel, loading }: Props) {
  const [name, setName] = useState(initialData?.name || '');
  const [description, setDescription] = useState(initialData?.description || '');
  const [triggerType, setTriggerType] = useState<TriggerConfig['type']>(initialData?.trigger?.type || 'manual');
  const [schedule, setSchedule] = useState(initialData?.trigger?.schedule || '');
  const [steps, setSteps] = useState<WorkflowStep[]>(initialData?.steps || []);
  const [editingStep, setEditingStep] = useState<WorkflowStep | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const workflow: CreateWorkflowInput = {
      name,
      description,
      trigger: { type: triggerType, schedule: triggerType === 'schedule' ? schedule : undefined },
      steps,
    };
    onSubmit(workflow);
  };

  const addStep = (type: WorkflowStep['type']) => {
    const newStep: WorkflowStep = {
      id: crypto.randomUUID(),
      name: `New ${type} step`,
      type,
      config: {},
      retryConfig: { maxRetries: 0, retryDelayMs: 1000 },
      timeoutMs: 60000,
    };
    setSteps([...steps, newStep]);
    setEditingStep(newStep);
  };

  const updateStep = (updated: WorkflowStep) => {
    setSteps(steps.map(s => s.id === updated.id ? updated : s));
    setEditingStep(null);
  };

  const removeStep = (id: string) => {
    setSteps(steps.filter(s => s.id !== id));
  };

  const moveStep = (id: string, direction: 'up' | 'down') => {
    const index = steps.findIndex(s => s.id === id);
    if (index === -1) return;
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === steps.length - 1) return;
    const newSteps = [...steps];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [newSteps[index], newSteps[targetIndex]] = [newSteps[targetIndex], newSteps[index]];
    setSteps(newSteps);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Workflow Name *</label>
        <input
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          required
          className="w-full px-3 py-2 border border-gray-300 rounded-md"
          placeholder="My Awesome Workflow"
        />
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
        <textarea
          value={description}
          onChange={e => setDescription(e.target.value)}
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-md"
          placeholder="What does this workflow do?"
        />
      </div>

      {/* Trigger */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Trigger Type</label>
        <select
          value={triggerType}
          onChange={e => setTriggerType(e.target.value as TriggerConfig['type'])}
          className="w-full px-3 py-2 border border-gray-300 rounded-md"
        >
          {TRIGGER_TYPES.map(t => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        {triggerType === 'schedule' && (
          <input
            type="text"
            value={schedule}
            onChange={e => setSchedule(e.target.value)}
            placeholder="* * * * * (cron)"
            className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md"
          />
        )}
      </div>

      {/* Steps */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Steps</label>
        <div className="space-y-2 mb-3">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center gap-2 p-3 bg-gray-50 rounded-md">
              <span className="text-sm text-gray-500">{index + 1}.</span>
              <span className={`px-2 py-0.5 text-xs rounded ${
                step.type === 'trigger' ? 'bg-green-100 text-green-700' :
                step.type === 'action' ? 'bg-blue-100 text-blue-700' :
                step.type === 'condition' ? 'bg-yellow-100 text-yellow-700' :
                'bg-gray-100 text-gray-700'
              }`}>
                {step.type}
              </span>
              <span className="flex-1 font-medium">{step.name}</span>
              <button type="button" onClick={() => moveStep(step.id, 'up')} className="text-gray-400 hover:text-gray-600">↑</button>
              <button type="button" onClick={() => moveStep(step.id, 'down')} className="text-gray-400 hover:text-gray-600">↓</button>
              <button type="button" onClick={() => setEditingStep(step)} className="text-blue-600 hover:text-blue-800">Edit</button>
              <button type="button" onClick={() => removeStep(step.id)} className="text-red-600 hover:text-red-800">×</button>
            </div>
          ))}
        </div>

        {/* Add step buttons */}
        <div className="flex flex-wrap gap-2">
          {STEP_TYPES.map(type => (
            <button
              key={type}
              type="button"
              onClick={() => addStep(type as WorkflowStep['type'])}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
            >
              + {type}
            </button>
          ))}
        </div>
      </div>

      {/* Step editor modal */}
      {editingStep && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Edit Step</h3>
            <StepEditor
              step={editingStep}
              onSave={updateStep}
              onCancel={() => setEditingStep(null)}
            />
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3">
        <button
          type="submit"
          disabled={loading || !name || steps.length === 0}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {loading ? 'Saving...' : initialData?.id ? 'Update Workflow' : 'Create Workflow'}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}

// Step editor sub-component
function StepEditor({ step, onSave, onCancel }: { step: WorkflowStep; onSave: (s: WorkflowStep) => void; onCancel: () => void }) {
  const [name, setName] = useState(step.name);
  const [maxRetries, setMaxRetries] = useState(step.retryConfig.maxRetries);
  const [timeoutMs, setTimeoutMs] = useState(step.timeoutMs);

  const handleSave = () => {
    onSave({
      ...step,
      name,
      retryConfig: { ...step.retryConfig, maxRetries },
      timeoutMs,
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Step Name</label>
        <input
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Max Retries</label>
        <input
          type="number"
          value={maxRetries}
          onChange={e => setMaxRetries(parseInt(e.target.value) || 0)}
          min={0}
          max={10}
          className="w-full px-3 py-2 border border-gray-300 rounded-md"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Timeout (ms)</label>
        <input
          type="number"
          value={timeoutMs}
          onChange={e => setTimeoutMs(parseInt(e.target.value) || 60000)}
          min={1000}
          step={1000}
          className="w-full px-3 py-2 border border-gray-300 rounded-md"
        />
      </div>

      <div className="flex gap-3">
        <button onClick={handleSave} className="px-4 py-2 bg-blue-600 text-white rounded-md">Save</button>
        <button onClick={onCancel} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md">Cancel</button>
      </div>
    </div>
  );
}
